import { supabaseClient } from '../supabase';
import { cacheManager } from '../cache/cacheManager';
import { SUPABASE_TABLES } from '../supabase/tables';
import type { SupportedLanguages } from '../../i18n';
import { logger } from '../logger';

class TranslationsClient {
  private static instance: TranslationsClient;
  private translationsCache: Map<string, Record<string, Record<SupportedLanguages, string>>> = new Map();
  private loadingPromises: Map<string, Promise<Record<string, Record<SupportedLanguages, string>>>> = new Map();

  private constructor() {}

  public static getInstance(): TranslationsClient {
    if (!TranslationsClient.instance) {
      TranslationsClient.instance = new TranslationsClient();
    }
    return TranslationsClient.instance;
  }

  public async getGeneralTranslations(): Promise<Record<string, Record<SupportedLanguages, string>>> {
    const cacheKey = 'general_translations';
    
    // Check local cache
    const cachedTranslations = this.translationsCache.get(cacheKey);
    if (cachedTranslations) {
      return cachedTranslations;
    }

    // Check persistent cache
    const persistedCache = cacheManager.get<Record<string, Record<SupportedLanguages, string>>>(cacheKey);
    if (persistedCache) {
      this.translationsCache.set(cacheKey, persistedCache);
      return persistedCache;
    }

    // Check if loading is already in progress
    let loadingPromise = this.loadingPromises.get(cacheKey);
    if (!loadingPromise) {
      loadingPromise = (async () => {
        try {
          const { data, error } = await supabaseClient
            .from(SUPABASE_TABLES.TRANSLATIONS.name)
            .select('*')
            .order('key');

          if (error) {
            throw error;
          }

          const translations = data.reduce((acc, record) => {
            if (record.key) {
              acc[record.key] = {
                fr: record.fr,
                en: record.en || record.fr,
                es: record.es || record.fr,
                it: record.it || record.fr,
                pt: record.pt || record.fr,
                de: record.de || record.fr,
                nl: record.nl || record.fr,
                ja: record.ja || record.fr,
                zh: record.zh || record.fr,
                ru: record.ru || record.fr
              };
            }
            return acc;
          }, {} as Record<string, Record<SupportedLanguages, string>>);

          // Add stay-related translations if they don't exist
          if (!translations['stay_dates']) {
            translations['stay_dates'] = {
              fr: 'Dates de séjour',
              en: 'Stay dates',
              es: 'Fechas de estancia',
              it: 'Date di soggiorno',
              pt: 'Datas de estadia',
              de: 'Aufenthaltsdaten',
              nl: 'Verblijfsdata',
              ja: '滞在日',
              zh: '住宿日期',
              ru: 'Даты пребывания'
            };
          }

          if (!translations['stay_explanation']) {
            translations['stay_explanation'] = {
              fr: 'Veuillez sélectionner les dates de votre séjour pour personnaliser votre expérience.',
              en: 'Please select your stay dates to personalize your experience.',
              es: 'Por favor, seleccione las fechas de su estancia para personalizar su experiencia.',
              it: 'Si prega di selezionare le date del soggiorno per personalizzare la tua esperienza.',
              pt: 'Por favor, selecione as datas da sua estadia para personalizar sua experiência.',
              de: 'Bitte wählen Sie Ihre Aufenthaltsdaten, um Ihr Erlebnis zu personalisieren.',
              nl: 'Selecteer uw verblijfsdata om uw ervaring te personaliseren.',
              ja: '滞在日を選択して、あなたの体験をカスタマイズしてください。',
              zh: '请选择您的住宿日期以个性化您的体验。',
              ru: 'Пожалуйста, выберите даты вашего пребывания, чтобы персонализировать ваш опыт.'
            };
          }

          if (!translations['validate']) {
            translations['validate'] = {
              fr: 'Valider',
              en: 'Validate',
              es: 'Validar',
              it: 'Convalidare',
              pt: 'Validar',
              de: 'Bestätigen',
              nl: 'Bevestigen',
              ja: '確認',
              zh: '确认',
              ru: 'Подтвердить'
            };
          }

          // Cache for 5 minutes
          cacheManager.set(cacheKey, translations, 5 * 60 * 1000);
          this.translationsCache.set(cacheKey, translations);

          return translations;
        } catch (error) {
          logger.error('Error loading translations', {
            category: 'Translation',
            data: { error }
          });
          return {};
        } finally {
          this.loadingPromises.delete(cacheKey);
        }
      })();

      this.loadingPromises.set(cacheKey, loadingPromise);
    }

    return loadingPromise;
  }

  public async getCategoryTypeTranslations(): Promise<Record<string, Record<SupportedLanguages, string>>> {
    const cacheKey = 'category_type_translations';
    
    // Check local cache
    const cachedTranslations = this.translationsCache.get(cacheKey);
    if (cachedTranslations) {
      return cachedTranslations;
    }

    // Check persistent cache
    const persistedCache = cacheManager.get<Record<string, Record<SupportedLanguages, string>>>(cacheKey);
    if (persistedCache) {
      this.translationsCache.set(cacheKey, persistedCache);
      return persistedCache;
    }

    // Check if loading is already in progress
    let loadingPromise = this.loadingPromises.get(cacheKey);
    if (!loadingPromise) {
      loadingPromise = (async () => {
        try {
          const { data, error } = await supabaseClient
            .from(SUPABASE_TABLES.CATEGORY_TRANSLATIONS.name)
            .select('*')
            .order('key');

          if (error) {
            throw error;
          }

          const translations = data.reduce((acc, record) => {
            if (record.key) {
              acc[record.key] = {
                fr: record.fr,
                en: record.en || record.fr,
                es: record.es || record.fr,
                it: record.it || record.fr,
                pt: record.pt || record.fr,
                de: record.de || record.fr,
                nl: record.nl || record.fr,
                ja: record.ja || record.fr,
                zh: record.zh || record.fr,
                ru: record.ru || record.fr
              };
            }
            return acc;
          }, {} as Record<string, Record<SupportedLanguages, string>>);

          // Cache for 5 minutes
          cacheManager.set(cacheKey, translations, 5 * 60 * 1000);
          this.translationsCache.set(cacheKey, translations);

          return translations;
        } catch (error) {
          logger.error('Error loading category translations', {
            category: 'Translation',
            data: { error }
          });
          return {};
        } finally {
          this.loadingPromises.delete(cacheKey);
        }
      })();

      this.loadingPromises.set(cacheKey, loadingPromise);
    }

    return loadingPromise;
  }

  public async preloadTranslations(): Promise<void> {
    try {
      await Promise.all([
        this.getGeneralTranslations(),
        this.getCategoryTypeTranslations()
      ]);
    } catch (error) {
      logger.error('Error preloading translations', {
        category: 'Translation',
        data: { error }
      });
    }
  }

  public clearCache(): void {
    this.translationsCache.clear();
    this.loadingPromises.clear();
    cacheManager.clear();
  }
}

export const translationsClient = TranslationsClient.getInstance();